import { Tag, Button, Upload, Tooltip } from "antd";
import React from "react";
import { FileOutlined, FileTextOutlined, PrinterOutlined, UploadOutlined, AlertOutlined } from '@ant-design/icons';
import { calculateExactAge, ConvertStringToDate, getUserDetails } from "../../utils/helper";
import moment from 'moment';
import { FieldLabels } from "../../constants/fieldLabels";

const statusColors = {
  'PENDING': 'red',
  'DRAFTED': 'orange',
  'REPORT_SUBMITTED': 'yellow',
  'SIGNEDOFF': 'green',
};

const iconStyle = {
  fontSize: '16px',
  cursor: 'pointer',
  color: '#4A4A4A',
};

export const orderColumns = ({ openReportEditor, role, addFile, viewNotes, printReport, captureReceiver }) => {
  const userDetails = getUserDetails();

  return ([
    {
      dataIndex: "patient.pat_name",
      title: FieldLabels.PATIENT_NAME,
      render: (text, record) => {
        const isEmergency = record.po_priority?.toUpperCase() === 'EMERGENCY';
        const patientName = record.patient?.pat_name;

        return (
          <div>
            <Button
              className="ms-1auto d-flex align-items-center ph-0"
              type="link"
              style={{ padding: 0 }}
            >
              <Tooltip title={patientName}>
                <span style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                  maxWidth: isEmergency ? 'calc(100% - 22px)' : '100%'
                }}>
                  {record.patient?.pat_name}
                </span>
              </Tooltip>
              {/* Emergency Icon */}
              {isEmergency && (
                <Tooltip title="Emergency Case">
                  <AlertOutlined
                    style={{
                      color: '#FF4D4F',
                      marginLeft: '6px',
                      fontSize: '14px',
                      animation: 'pulse 2s ease-in-out infinite'
                    }}
                  />
                </Tooltip>
              )}
            </Button>
          </div>
        )
      },
      width: 250
    },
    {
      dataIndex: "po_diag_desc",
      title: FieldLabels.STUDY_DESCRIPTION,
      width: 200,
      render: (txt, rec) => {
        const diagDesc = rec?.po_diag_desc;
        const priority = rec?.po_priority;
        return (
          <Tooltip title={`${diagDesc}${priority ? ` (Priority: ${priority})` : ''}`}>
            <div style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%'
            }}>
              {diagDesc}
            </div>
          </Tooltip>
        )
      },
    },
    {
      dataIndex: "patient.pat_pin",
      title: FieldLabels.PATIENT_ID,
      width: 105,
      render: (text, rec) => {
        const patPin = rec?.patient?.pat_pin
        return (
          <Tooltip title={patPin}>
            <div style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%'
            }}>
              {patPin}
            </div>
          </Tooltip>
        );
      }
    },
    {
      dataIndex: "po_his_ord_no",
      title: FieldLabels.ORDER_NUMBER,
      width: 100,
      render: (text, rec) => {
        const orderNo = rec?.po_his_ord_no;
        return (
          <Tooltip title={orderNo}>
            <div style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%'
            }}>
              {orderNo}
            </div>
          </Tooltip>
        );
      }
    },
    {
      dataIndex: "po_acc_no",
      title: FieldLabels.ACCESSION_NUMBER,
      width: 150,
      render: (text, rec) => {
        const accNo = rec?.po_acc_no;
        return (
          <Tooltip title={accNo}>
            <div style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%'
            }}>
              {accNo}
            </div>
          </Tooltip>
        );
      }
    },
    {
      dataIndex: "patient.pat_age",
      title: FieldLabels.PATIENT_AGE,
      width: 100,
      render: (val, record) => {
        return calculateExactAge(record.patient?.pat_dob)
      }
    },
    {
      dataIndex: "patient.pat_sex",
      title: FieldLabels.PATIENT_SEX,
      width: 100,
      render: (text, rec) => rec?.patient?.pat_sex
    },
    {
      dataIndex: "po_site",
      title: FieldLabels.SITE,
      width: 150,
      render: (text, rec) => {
        const site = rec?.po_site || 'SOMAJIGUDA';
        return (
          <Tooltip title={site}>
            <div style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%'
            }}>
              {site}
            </div>
          </Tooltip>
        );
      }
    },
    {
      dataIndex: "po_ref_doc",
      title: FieldLabels.REFERRING_DOCTOR,
      width: 175,
      render: (text, rec) => {
        const refDoc = rec?.po_ref_doc;
        return (
          <Tooltip title={refDoc}>
            <div style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
              textAlign: refDoc ? 'left' : 'center'
            }}>
              {refDoc || '-'}
            </div>
          </Tooltip>
        );
      }
    },
    {
      dataIndex: "po_req_time",
      title: FieldLabels.SCAN_DATE,
      render: (val, record) => {
        return (
          <span>{val ? moment(val).utcOffset('+05:30').format("Do MMM YYYY, hh:mmA") : '-'}</span>
        )
      },
      width: 200
    },
    {
      dataIndex: "order_workflow.ow_pacs_status",
      title: FieldLabels.STATUS,
      fixed: 'right',
      className: 'first-sticky-column',
      render: (text, record) => {
        const dispatch_status = record?.order_workflow?.ow_pacs_status;
        return (
          <Tag color={statusColors[dispatch_status]}>{dispatch_status?.replaceAll('_', ' ')}</Tag>
        )
      },
      width: 100
    },
    {
      title: FieldLabels.ACTIONS,
      key: "actions_column",
      fixed: 'right',
      render: (text, record) => {
        const canPrintReport = record?.order_workflow?.ow_pacs_status === 'SIGNEDOFF';
        return (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '4px 6px',
          }}>
            {/* Print Report - Only if Signed Off */}
            <Tooltip title={canPrintReport ? 'Print Report' : 'Report not signed off'}>
              <span
                style={{ cursor: canPrintReport ? 'pointer' : 'not-allowed', opacity: canPrintReport ? 1 : 0.5 }}
                onClick={(e) => { e.stopPropagation(); canPrintReport && printReport(record); }}
              >
                <PrinterOutlined style={iconStyle} />
              </span>
            </Tooltip>
          </div>
        );
      },
      width: 100
    }
  ].filter(itm => !itm.hidden))
};
